import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Lock, MessagesSquare } from "lucide-react";
import { Fragment, useEffect, useRef, type ReactNode } from "react";

import { acrescentarMensagem, LIMITE_DA_MENSAGEM } from "@/components/ChatPanel";
import { PageError } from "@/components/PageState";
import { EmptyState } from "@/components/ui/empty-state";
import { AvatarDoResponsavel } from "@/components/usuarios/AvatarDoResponsavel";
import { criarClienteDeChat, type ChatMessage } from "@/lib/chat-api";
import { carregarUltimasMensagens } from "@/lib/chat-ultimas-mensagens";
import { contaDaFoto } from "@/lib/conversas";
import { iniciaisDoNome } from "@/lib/ficha-da-equipe";
import { formatClock } from "@/lib/format";
import { contagem } from "@/lib/plural";
import type { ConversaUnificada, RespostaPronta } from "@/lib/types";
import { cn } from "@/lib/utils";

import { CabecalhoDaConversa } from "./CabecalhoDaConversa";
import { CompositorDaCaixa, type RascunhosDaCaixa } from "./CompositorDaCaixa";

/**
 * TUT-590 — a conversa numa janela do chat rápido: os ids são da janela (cabem
 * três abertas), o topo traz as ações dela, e ficam de fora as respostas
 * prontas (decisão do dono: só lista, conversa e envio).
 */
export interface JanelaDaConversa {
  idDoTitulo: string;
  idDoCampo: string;
  acoes: ReactNode;
}

/** O `@Nome` de quem foi mencionado aparece realçado na bolha. */
function comMencoes(corpo: string, nomes: readonly string[]) {
  if (!nomes.length) return corpo;
  const padrao = new RegExp(`(${nomes.map((n) => `@${n.replace(/[.*+?^${}()|[\]\\]/gu, "\\$&")}`).join("|")})`, "gu");
  return corpo.split(padrao).map((parte, i) =>
    nomes.some((n) => parte === `@${n}`) ? (
      <mark key={i} className="rounded bg-[var(--primary-highlight)] px-0.5 font-semibold text-[var(--primary)]">
        {parte}
      </mark>
    ) : (
      <Fragment key={i}>{parte}</Fragment>
    ),
  );
}

/**
 * TUT-491 — a conversa do chat (Equipe ou Portal) no meio da caixa única. Na
 * Equipe, o `@` chama quem participa (e põe a conversa em "Minhas" de quem foi
 * chamado, C3). No Portal o externo lê tudo: sem menção, e com as respostas
 * prontas do escritório (C5).
 *
 * TUT-545 — a direta ou o grupo de antes com alguém de fora vem
 * `somenteLeitura`: o histórico fica à vista, e no lugar da caixa de escrever
 * uma frase diz que a conversa foi encerrada e por quê (postar ali a API
 * recusa com 409).
 */
export function ConversaInterna({
  conversa,
  meuId,
  rascunhos,
  respostas,
  onMudou,
  onVoltar,
  onContato,
  janela,
}: {
  conversa: ConversaUnificada;
  meuId: string;
  /** Avisa a caixa (recarrega só a primeira página, M1). */
  onMudou: () => void;
  rascunhos: RascunhosDaCaixa;
  respostas: readonly RespostaPronta[] | null;
  onVoltar?: () => void;
  onContato?: () => void;
  /** TUT-590 — a conversa numa janela do chat rápido (ver `JanelaDaConversa`). */
  janela?: JanelaDaConversa;
}) {
  const client = criarClienteDeChat(false);
  const queryClient = useQueryClient();
  const id = conversa.id.slice(5);
  const chaveDasMensagens = ["chat", "messages", false, id] as const;
  const lista = useRef<HTMLDivElement | null>(null);

  const mensagens = useQuery({
    queryKey: chaveDasMensagens,
    queryFn: () => carregarUltimasMensagens(client, id),
  });
  const marcarLida = useMutation({
    mutationFn: () => client.markRead(id),
    onSuccess: () => onMudou(),
  });
  const enviar = useMutation({
    mutationFn: (valor: { texto: string; mencoes: string[] }) => client.postMessage(id, valor.texto, valor.mencoes),
    onSuccess: (mensagem) => {
      queryClient.setQueryData<{ items: ChatMessage[]; total: number }>(chaveDasMensagens, (atual) =>
        acrescentarMensagem(atual, mensagem),
      );
      onMudou();
    },
  });

  // Abrir a conversa a marca como lida — a mesma regra do chat de sempre.
  useEffect(() => {
    marcarLida.mutate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    if (lista.current) lista.current.scrollTop = lista.current.scrollHeight;
  }, [mensagens.data]);

  const portal = conversa.canal === "portal";
  const outros = conversa.participantes.filter((p) => p.userId !== meuId);
  const externo = conversa.participantes.find((p) => p.externo);
  const nomes = conversa.participantes.map((p) => p.nome);
  const subtitulo = conversa.somenteLeitura
    ? "Só para consulta"
    : portal
    ? `${externo?.nome ?? "Quem usa o portal"} vê tudo o que for escrito aqui`
    : conversa.tipo === "GROUP"
      ? contagem(conversa.participantes.length, "participante", "participantes")
      : "Conversa direta";

  return (
    <>
      <CabecalhoDaConversa
        conversa={conversa}
        subtitulo={subtitulo}
        onVoltar={onVoltar}
        onContato={onContato}
        acoes={janela?.acoes}
        idDoTitulo={janela?.idDoTitulo}
      />
      <div
        ref={lista}
        role="log"
        aria-label="Mensagens"
        className={cn(
          "flex flex-1 flex-col gap-3 overflow-y-auto bg-surface-2",
          janela ? "min-h-0 p-3" : "min-h-[18rem] p-4",
        )}
      >
        {mensagens.isLoading ? (
          <p className="muted m-0">Carregando mensagens…</p>
        ) : mensagens.isError ? (
          <PageError message="Não foi possível ler esta conversa." onRetry={() => void mensagens.refetch()} />
        ) : mensagens.data?.items.length ? (
          mensagens.data.items.map((mensagem) => {
            const minha = mensagem.authorUserId === meuId;
            // TUT-561 — quem escreveu, ao lado da bolha de quem não sou eu (o
            // molde do portal, TUT-558): a foto da equipe, ou as iniciais. Quem
            // é de fora entra sem conta, e a foto dele nunca é pedida.
            const autor = conversa.participantes.find((p) => p.userId === mensagem.authorUserId);
            return (
              <div
                key={mensagem.id}
                data-mensagem={mensagem.id}
                className={cn("flex max-w-[88%] min-w-0 items-end gap-2", minha ? "ml-auto" : "mr-auto")}
              >
                {!minha ? (
                  <AvatarDoResponsavel
                    userId={autor ? contaDaFoto(autor) : mensagem.authorUserId}
                    nome={mensagem.authorName}
                    iniciais={iniciaisDoNome(mensagem.authorName)}
                    rotulo=""
                  />
                ) : null}
                <article
                  className={cn(
                    "min-w-0 rounded-xl border border-border px-3 py-2 shadow-sm",
                    minha ? "bg-[var(--primary-highlight)]" : "bg-surface",
                  )}
                >
                  <p className="m-0 whitespace-pre-wrap break-words text-corpo">
                    {mensagem.mentionedUserIds?.length ? comMencoes(mensagem.body, nomes) : mensagem.body}
                  </p>
                  <footer className="mt-1 flex justify-end gap-2 text-legenda text-[var(--muted)]">
                    {!minha ? <span>{mensagem.authorName}</span> : null}
                    <time>{formatClock(new Date(mensagem.createdAt))}</time>
                  </footer>
                </article>
              </div>
            );
          })
        ) : (
          <EmptyState>
            <div>
              <MessagesSquare className="mx-auto size-8" aria-hidden="true" />
              <p>A conversa ainda não tem mensagens.</p>
            </div>
          </EmptyState>
        )}
      </div>
      {conversa.somenteLeitura ? (
        <p className="m-0 flex items-start gap-2 border-t border-border p-3 text-corpo text-[var(--muted)]" data-conversa-encerrada>
          <Lock aria-hidden="true" className="mt-[0.15rem] size-4 shrink-0" />
          <span>
            Conversa encerrada: {externo?.nome ?? "quem é de fora"} agora fala com o escritório pela conversa do portal. O
            histórico fica aqui para consulta.
          </span>
        </p>
      ) : (
      <CompositorDaCaixa
        key={conversa.id}
        conversaId={conversa.id}
        rascunhos={rascunhos}
        idDoCampo={janela?.idDoCampo ?? "chat-composer"}
        compacto={Boolean(janela)}
        limite={LIMITE_DA_MENSAGEM}
        enviando={enviar.isPending}
        respostas={portal && !janela ? respostas : null}
        contexto={{ nome: externo?.nome ?? null, processo: null }}
        mencionaveis={portal ? null : outros.map((p) => ({ userId: p.userId, nome: p.nome }))}
        rotulo={() => (portal ? "Mensagem no portal" : "Escrever mensagem")}
        placeholder={() =>
          portal
            ? janela
              ? "Escreva uma mensagem..."
              : "Escreva uma mensagem... (/ para respostas prontas)"
            : "Escreva para a equipe... (@ para mencionar)"
        }
        textoDoBotao={() => "Enviar"}
        aviso={() =>
          portal
            ? `${externo?.nome ?? "Quem usa o portal"} vê tudo o que for escrito aqui. Para combinar algo só com a equipe, use uma conversa da Equipe.`
            : "Só os participantes veem. Use @ para avisar alguém."
        }
        onEnviar={(texto, _modo, mencoes) => enviar.mutateAsync({ texto, mencoes })}
      />
      )}
    </>
  );
}
